import type { AssignmentInput, Role } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { directionsQuery, projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button, IconButton } from '../../ui/button';
import { Select } from '../../ui/form';

/** Роли сотрудника: роль + область — вся компания, юрлицо, направление, проект или подпроект (ДС-03). */
export function AssignmentsEditor({
  roles,
  value,
  onChange,
}: {
  roles: Role[];
  value: AssignmentInput[];
  onChange: (v: AssignmentInput[]) => void;
}) {
  const me = useMe();
  const dirs = useQuery(directionsQuery);
  const projects = useQuery(projectsQuery('all'));
  const scopeKey = (a: AssignmentInput) =>
    a.scopeType === 'tenant' ? 'tenant' : `${a.scopeType}:${a.scopeId}`;

  function update(i: number, patch: Partial<AssignmentInput>) {
    onChange(value.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  }

  return (
    <div className="flex flex-col gap-2">
      {value.map((a, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: строки редактора не имеют собственного id
          key={`${i}-${a.roleId}`}
          className="grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[1.3fr_1fr_auto]"
        >
          <Select
            aria-label="Роль"
            value={a.roleId}
            onChange={(e) => update(i, { roleId: e.target.value })}
            className="col-span-2 sm:col-span-1"
          >
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Где действует роль"
            value={scopeKey(a)}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'tenant') update(i, { scopeType: 'tenant', scopeId: null });
              else {
                const [type, id] = v.split(':');
                update(i, { scopeType: type as AssignmentInput['scopeType'], scopeId: id ?? null });
              }
            }}
          >
            <option value="tenant">Вся компания</option>
            {me.legalEntities.map((le) => (
              <option key={le.id} value={`legal_entity:${le.id}`}>
                Юрлицо {le.shortName}
              </option>
            ))}
            {(dirs.data ?? []).length ? (
              <optgroup label="Направления">
                {(dirs.data ?? []).map((d) => (
                  <option key={d.id} value={`direction:${d.id}`}>
                    {d.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {(projects.data ?? []).length ? (
              <optgroup label="Проекты и подпроекты">
                {(projects.data ?? []).map((p) => (
                  <option key={p.id} value={`project:${p.id}`}>
                    {p.parentId ? '— ' : ''}
                    {p.code} {p.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </Select>
          <IconButton label="Убрать роль" onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      ))}
      <div>
        <Button
          size="sm"
          variant="ghost"
          icon={<Plus className="size-4" />}
          onClick={() =>
            roles[0] && onChange([...value, { roleId: roles[0].id, scopeType: 'tenant', scopeId: null }])
          }
        >
          Добавить роль
        </Button>
      </div>
    </div>
  );
}
